import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatCurrency, calcularEquivalenteMensual } from '@/lib/utils'
import type { ServicioActivoCliente } from '@/services/clientesService'

interface ServiciosBadgeListProps {
  servicios: ServicioActivoCliente[]
  max?: number
  /** Muestra el precio de cada servicio y el total mensual en el detalle. */
  mostrarPrecios?: boolean
}

/**
 * Muestra una lista de servicios como badges compactos.
 * Si hay más servicios que el máximo, muestra un popover con el detalle completo.
 */
export function ServiciosBadgeList({ servicios, max = 2, mostrarPrecios = false }: ServiciosBadgeListProps) {
  if (!servicios || servicios.length === 0) {
    return <span className="text-muted-foreground text-sm">-</span>
  }

  const mostrar = servicios.slice(0, max)
  const ocultos = servicios.length - max

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {mostrar.map((servicio) => (
        <Badge
          key={servicio.servicio_id}
          variant="outline"
          className="text-xs font-normal"
        >
          {servicio.nombre}
        </Badge>
      ))}
      {ocultos > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-xs text-primary hover:underline cursor-pointer font-medium"
              aria-label={`Ver ${ocultos} servicios más`}
            >
              +{ocultos}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-auto max-w-xs p-3">
            <ServiciosDetalleList servicios={servicios} mostrarPrecios={mostrarPrecios} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

interface ServiciosDetalleListProps {
  servicios: ServicioActivoCliente[]
  mostrarPrecios: boolean
}

/**
 * Muestra la lista completa de servicios con precio y frecuencia en un popover
 */
function ServiciosDetalleList({ servicios, mostrarPrecios }: ServiciosDetalleListProps) {
  // Calcular total mensual
  const totalMensual = servicios.reduce(
    (sum, s) => sum + calcularEquivalenteMensual(s.precio, s.frecuencia),
    0
  )

  // Labels de frecuencia
  const frecuenciaLabels: Record<string, string> = {
    mensual: 'mes',
    bimestral: 'bim',
    trimestral: 'trim',
    semestral: 'sem',
    anual: 'año',
    unico: 'único',
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase">
        Servicios Contratados
      </p>
      <ul className="space-y-1.5">
        {servicios.map((s) => {
          const frecLabel = frecuenciaLabels[s.frecuencia] || s.frecuencia
          return (
            <li key={s.servicio_id} className="flex items-start justify-between gap-3 text-sm">
              <span className="flex-1 min-w-0">
                • <span className="font-medium">{s.nombre}</span>
              </span>
              {mostrarPrecios && (
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                  {formatCurrency(s.precio)}/{frecLabel}
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {mostrarPrecios && (
        <div className="pt-2 border-t flex items-center justify-between">
          <span className="text-xs font-semibold">Total Mensual</span>
          <span className="text-sm font-bold tabular-nums">{formatCurrency(totalMensual)}</span>
        </div>
      )}
    </div>
  )
}
